import { cva } from 'class-variance-authority'

/**
 * Geometry shared by every DSM button.
 *
 * Height, radius, weight and the icon size scale live here and nowhere else,
 * so a primary and a secondary button placed side by side always line up.
 * Labels are sentence case — write them that way in the source, there is no
 * `uppercase` transform doing it for you.
 * Only colour differs between the two components — see `cta-button.tsx` and
 * `secondary-button.tsx`.
 *
 * Sizes map to the three places a CTA actually appears:
 * - `sm` toolbars and dense rows sitting inside a card
 * - `md` the default page action
 * - `lg` hero and full-width bars
 */
export const dsmButtonBase = cva(
  [
    'inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg',
    'font-semibold transition-colors',
    // Hover is a colour change, never a scale: these buttons sit in rows at
    // gap-2, where a scaled button overlaps its neighbours. The press is a 1px
    // nudge — it costs no layout, and it is the only feedback a touch device
    // gets, since there is no hover there at all.
    'active:translate-y-px',
    'outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'disabled:pointer-events-none disabled:opacity-60',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  ],
  {
    variants: {
      size: {
        sm: 'h-8 px-3 text-xs [&_svg]:size-3.5',
        md: 'h-10 px-4 text-sm [&_svg]:size-4',
        lg: 'h-12 px-6 text-base [&_svg]:size-4',
      },
      fullWidth: {
        true: 'w-full',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
)
